import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Search, BookOpen } from "lucide-react";
import { useApi, fmtDate } from "@/lib/api";
import { Markdown } from "@/components/Markdown";
import { TagChips } from "@/components/AudiencePanel";
import { Tt } from "@/components/Tt";
import { t } from "@/i18n";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

function OiItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-[var(--border-color)] first:border-t-0" data-testid={`oi-item-${item.id}`}>
      <button className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg-surface-hover)] transition-colors" aria-expanded={open} onClick={() => setOpen(!open)} data-testid={`oi-item-toggle-${item.id}`}>
        <span className="flex-1 min-w-0 font-medium truncate"><Tt text={item.title} /></span>
        <TagChips tags={item.tags} />
        <ChevronDown size={16} className={`shrink-0 faint transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 stack-sm" data-testid={`oi-item-body-${item.id}`}>
          <p className="text-xs faint">v{item.version} · {t("Updated")} {fmtDate(item.updated_at)}</p>
          <div className="prose-sm max-w-none"><Markdown>{item.markdown}</Markdown></div>
          <Link to={`/sops/${item.id}`} className="btn btn-ghost btn-sm self-start" data-testid={`oi-item-open-${item.id}`}>{t("Open full page")}</Link>
        </div>
      )}
    </div>
  );
}

function TopicGroup({ group, open, onToggle }) {
  return (
    <div className="card !p-0 overflow-hidden" data-testid={`oi-topic-${slug(group.topic)}`}>
      <button className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-[var(--bg-surface-hover)]/40 hover:bg-[var(--bg-surface-hover)] transition-colors" aria-expanded={open} onClick={onToggle} data-testid={`oi-topic-toggle-${slug(group.topic)}`}>
        <span className="flex-1 font-semibold">{t(group.topic)}</span>
        <span className="chip chip-muted">{group.items.length}</span>
        <ChevronDown size={18} className={`faint transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div>{group.items.map((it) => <OiItem key={it.id} item={it} />)}</div>}
    </div>
  );
}

export function OiTopics() {
  const api = useApi();
  const [groups, setGroups] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});
  useEffect(() => { api.get("/oi/topics").then((r) => setGroups(r.data)).catch(() => setGroups([])); }, [api]);
  const shown = (groups || []).map((g) => ({ ...g, items: g.items.filter((i) => `${i.title} ${i.markdown}`.toLowerCase().includes(q.toLowerCase())) }))
    .filter((g) => g.items.length);
  const total = (groups || []).reduce((n, g) => n + g.items.length, 0);
  return (
    <section className="max-w-3xl mx-auto px-4 pb-24 stack" data-testid="oi-topics">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2"><BookOpen size={20} /> {t("Your OI library")}</h2>
          <p className="text-sm faint">{t("Everything you can read in this organization, by topic · {n}", { n: total })}</p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 faint" />
          <input className="field !pl-9 !py-2 text-sm" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search procedures")} data-testid="oi-topics-search" />
        </div>
      </div>
      {groups === null ? <p className="faint" data-testid="oi-topics-loading">{t("Sorting your OI by topic…")}</p>
        : shown.length === 0 ? <p className="card text-sm faint" data-testid="oi-topics-empty">{t("Nothing here.")}</p>
        : shown.map((g) => <TopicGroup key={g.topic} group={g} open={!!q || !!open[g.topic]} onToggle={() => setOpen({ ...open, [g.topic]: !open[g.topic] })} />)}
    </section>
  );
}
